/**
 * Shared types for jgolf.
 */

// ─────────────────────────────────────────────────────────────
// Geometry
// ─────────────────────────────────────────────────────────────
export interface Vec2 {
  x: number;
  y: number;
}

/** @deprecated kept as an alias for older call sites */
export type Point = Vec2;
export type BallState = Vec2;

// ─────────────────────────────────────────────────────────────
// Surfaces — the unified collision-map code space (authentic).
// Elements occupy 0..23; specials occupy (specialIndex + 24) = 24..47.
// ─────────────────────────────────────────────────────────────
export enum Surface {
  // Elements (terrain / walls)
  Grass = 0,
  Dirt = 1,
  Mud = 2,
  Ice = 3,
  SpeedN = 4,
  SpeedNE = 5,
  SpeedE = 6,
  SpeedSE = 7,
  SpeedS = 8,
  SpeedSW = 9,
  SpeedW = 10,
  SpeedNW = 11,
  Water = 12,
  Acid = 13,
  WaterSwamp = 14,
  AcidSwamp = 15,
  Block = 16,
  StickyBlock = 17,
  BouncyBlock = 18,
  FakeBlock = 19,
  OneWayN = 20,
  OneWayE = 21,
  OneWayS = 22,
  OneWayW = 23,
  // Specials (specialIndex + 24)
  Start = 24,
  Hole = 25,
  FakeHole = 26,
  MoveableBlock = 27,
  Mine = 28,
  BlownMine = 29,
  BigMine = 30,
  BlownBigMine = 31,
  TeleBlueIn = 32,
  TeleBlueOut = 33,
  TeleRedIn = 34,
  TeleRedOut = 35,
  TeleYellowIn = 36,
  TeleYellowOut = 37,
  TeleGreenIn = 38,
  TeleGreenOut = 39,
  Breakable4 = 40, // full
  Breakable3 = 41,
  Breakable2 = 42,
  Breakable1 = 43,
  MagnetAttract = 44,
  MagnetRepel = 45,
  MoveableBlock2 = 46,
  SunkMoveable = 47,
  StartBlue = 48,
  StartRed = 49,
  StartYellow = 50,
  StartGreen = 51,
}

/** 15x15 sub-tile shape indices (foreground geometry within a tile). */
export enum Shape {
  Blank = 0,
  BigCircle = 1,
  SmallCircle = 2,
  Diamond = 3,
  TriangleSE = 4,
  TriangleSW = 5,
  TriangleNW = 6,
  TriangleNE = 7,
  RoundedSE = 8,
  RoundedSW = 9,
  RoundedNW = 10,
  RoundedNE = 11,
  RoundedS = 12,
  RoundedE = 13,
  RoundedN = 14,
  RoundedW = 15,
  TriangleN = 16,
  TriangleE = 17,
  TriangleS = 18,
  TriangleW = 19,
  TriangleNS = 20,
  TriangleWE = 21,
  HalfW = 22,
  HalfS = 23,
  QuarterNE = 24,
  QuarterSE = 25,
  QuarterSW = 26,
  QuarterNW = 27,
}

/** A single grid tile. Either a "normal" tile (shape over fg/bg) or a special. */
export interface Tile {
  /** Special index 24..51, or null for a normal tile. */
  special: Surface | null;
  /** Shape index (normal tiles only). */
  shape: Shape;
  /** Background element (0..23). */
  bg: Surface;
  /** Foreground element shown where the shape mask is set (normal tiles only). */
  fg: Surface;
}

// ─────────────────────────────────────────────────────────────
// Tracks / courses
// ─────────────────────────────────────────────────────────────
export type TrackCategory = 'basic' | 'traditional' | 'modern' | 'holeinone' | 'short' | 'long';
export type CourseDifficulty = 'easy' | 'medium' | 'hard';

export interface TrackSettings {
  minPlayers: number;
  maxPlayers: number;
  /** WATER_RESET_TEE | WATER_RESET_SHORE */
  waterReset: number;
  /** Whether balls collide with each other. */
  collision: boolean;
}

/** A single hole ("track"). */
export interface Track {
  id: string;
  name: string;
  author: string;
  /** Flat row-major array of MAP_COLS*MAP_ROWS tiles. */
  tiles: Tile[];
  /** Tee position (native px). Falls back to the Start tile if omitted. */
  tee: Vec2;
  /** Cup position (native px). Falls back to the Hole tile if omitted. */
  cup: Vec2;
  par: number;
  categories: TrackCategory[];
  difficulty: CourseDifficulty;
}

/** An ordered set of tracks played as one round. */
export interface CourseDefinition {
  id: string;
  name: string;
  difficulty: CourseDifficulty;
  trackIds: string[];
}

// ─────────────────────────────────────────────────────────────
// Strokes & simulation
// ─────────────────────────────────────────────────────────────
export enum ShootingMode {
  Normal = 0,
  Reverse = 1,
  Right = 2,
  Left = 3,
}

/** Player's stroke intent sent to the server. */
export interface StrokeIntent {
  /** Aim vector = (mouse - ball), in native px. The server derives power. */
  dx: number;
  dy: number;
  mode: ShootingMode;
}

/** One ball's state inside the simulator. */
export interface SimBall {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  holed: boolean;
  /** Strokes already taken on this hole before the simulated stroke. */
  strokesThisHole?: number;
  /** Sank in water/acid this stroke (reset afterwards). */
  drowned: boolean;
}

/** A replay sample: positions of all balls at one moment. */
export interface PathSample {
  /** Per-ball [x, y] in the same order as the result's `balls`. */
  p: Array<[number, number]>;
  dt: number; // ms since previous sample
}

export type StrokeEventType = 'wall' | 'bounce' | 'sink' | 'water' | 'teleport' | 'mine' | 'collide';

export interface StrokeEvent {
  t: StrokeEventType;
  x: number;
  y: number;
  /** Frame index the event happened on (for replay timing). */
  frame: number;
}

/** Final per-ball outcome of a stroke. */
export interface BallOutcome {
  id: string;
  x: number;
  y: number;
  holed: boolean;
  /** True if the ball was sunk this stroke (so it counts as holed). */
  sankThisStroke: boolean;
  /** True if drowned (server applies the reset below). */
  drowned: boolean;
  /**
   * Where a drowned ball respawns (authentic): water/water-swamp returns the
   * ball to where this stroke started; acid returns it to the tee.
   */
  resetX?: number;
  resetY?: number;
}

/**
 * A persistent change to the course caused by a stroke (authentic): mines
 * become blown craters, breakable walls crack and finally shatter, moveable
 * blocks slide (and sink into water). Applied identically by the server and
 * every client, keyed to the tile grid.
 */
export interface MapMutation {
  col: number;
  row: number;
  /** New special surface for the tile, or null to clear it (tile becomes bg). */
  to: Surface | null;
  /** Frame the change happened on (for replay-synchronized repaints). */
  frame: number;
}

/** Result of simulating one stroke (server-authoritative, deterministic). */
export interface StrokeResult {
  /** Ball ids in sample order. */
  ballIds: string[];
  samples: PathSample[];
  outcomes: BallOutcome[];
  events: StrokeEvent[];
  /** Persistent course changes (blown mines, broken walls, moved blocks). */
  mutations: MapMutation[];
  /** Total frames simulated. */
  frames: number;
}

// ─────────────────────────────────────────────────────────────
// Game / room state mirrored to clients
// ─────────────────────────────────────────────────────────────
export type GamePhase = 'lobby' | 'aiming' | 'simulating' | 'hole_complete' | 'round_over';

export interface PlayerView {
  sessionId: string;
  name: string;
  colorIndex: number;
  ball: Vec2;
  strokesThisHole: number;
  strokesTotal: number;
  holed: boolean;
  connected: boolean;
  isBot: boolean;
  /** Per-hole scores recorded so far (index = hole number). */
  holeScores: number[];
}

// ─────────────────────────────────────────────────────────────
// Network messages
// ─────────────────────────────────────────────────────────────
export interface HoleDataMessage {
  trackId: string;
  name: string;
  par: number;
  holeIndex: number;
  holeCount: number;
  tee: Vec2;
  cup: Vec2;
  /** Run-length-friendly flat tile codes (see track codec). */
  tiles: number[];
  settings: TrackSettings;
}

export interface StrokeResultMessage {
  by: string;
  result: StrokeResult;
  nextTurn: string;
  phase: GamePhase;
}

export interface TurnMessage {
  currentTurn: string;
  /** Epoch ms when the turn auto-skips, or 0 if no timer. */
  deadline: number;
  holeIndex: number;
}

export interface GameOverMessage {
  reason: 'complete' | 'forfeit';
  winnerId?: string;
  players: PlayerView[];
}

// ─────────────────────────────────────────────────────────────
// Lobby / matchmaking (REST)
// ─────────────────────────────────────────────────────────────
export interface LobbyUser {
  userId: string;
  name: string;
  status: 'idle' | 'playing' | 'away';
  isBot?: boolean;
}

export interface LobbyMessage {
  id: string;
  name: string;
  text: string;
  ts: number;
}

export interface Challenge {
  id: string;
  fromId: string;
  fromName: string;
  toId: string;
  ts: number;
  status: 'pending' | 'accepted' | 'declined';
  roomId?: string;
  botGame?: boolean;
}

export interface MatchCreateResponse {
  roomId: string;
  code: string;
}

export interface JoinOptions {
  name: string;
  token?: string;
  spectate?: boolean;
  code?: string;
}

// ─────────────────────────────────────────────────────────────
// Persistence
// ─────────────────────────────────────────────────────────────
export interface PlayerStats {
  gamesPlayed: number;
  gamesWon: number;
  totalStrokes: number;
  bestRound?: number;
  holesInOne: number;
}

export interface LeaderboardEntry {
  id: string;
  nickname: string;
  gamesWon: number;
  gamesPlayed: number;
  holesInOne: number;
}

// ─────────────────────────────────────────────────────────────
// Connectivity (client)
// ─────────────────────────────────────────────────────────────
export type ConnectionState = 'connected' | 'connecting' | 'reconnecting' | 'disconnected' | 'failed';

export interface GameSession {
  roomId: string;
  sessionId: string;
  reconnectionToken?: string;
  gamePhase: string;
  createdAt: number;
  lastActiveAt: number;
}
